import { Chip, Stack, type Theme, Tooltip, Typography } from '@mui/material'
import React from 'react'

import Icon from 'components/Icon'
import { pluralize } from 'utils/strings'

// limit the width of chips at smaller screen sizes so that it doesn't overflow the k8s object name
// this is a little tricky here because there's also responsive behavior that causes
// the RM 101 video on the right to move down below at certain sizes
// so there are some cases where the width could be bigger, but this is good enough for now
const responsiveChipStyle = (muiTheme: Theme) => ({
  [muiTheme.breakpoints.down('xl')]: {
    maxWidth: 100,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  [muiTheme.breakpoints.only('xl')]: {
    maxWidth: 200,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  }
})

export const KubernetesListItemTags: React.FC<{
  isSelected?: boolean
  tags: Record<string, any>
}> = ({ isSelected, tags }) => {
  const {
    cluster,
    namespace,
    podCount,
    replicaSetCount,
    replicationControllerCount,
    unhealthyPods
  } = tags

  const hasPods = podCount > 0
  const hasReplicaSets = replicaSetCount > 0
  const hasReplicationControllers = replicationControllerCount > 0
  const color = isSelected ? 'primary' : 'default'

  return (
    <Stack direction="row" gap={1}>
      {unhealthyPods.length > 0 && (
        <Tooltip
          arrow
          placement="top"
          title={`${unhealthyPods} ${
            unhealthyPods.length === 1 ? 'is' : 'are'
          } not healthy. Your attack may not be successful.`}
        >
          <Typography color="warning.main">
            <Icon
              data-test-tag-selection-warning-icon
              css={{ height: 24 }}
              icon="warning"
            />
          </Typography>
        </Tooltip>
      )}
      <Tooltip arrow placement="top" title={`Cluster: ${cluster}`}>
        <Chip
          color={color}
          data-testid="kubernetes-cluster-chip"
          label={
            <Typography noWrap fontFamily="monospace" variant="caption">
              {cluster}
            </Typography>
          }
          role="status"
          size="small"
          sx={responsiveChipStyle}
          variant="outlined"
        />
      </Tooltip>
      <Tooltip arrow placement="top" title={`Namespace: ${namespace}`}>
        <Chip
          color={color}
          data-testid="kubernetes-namespace-chip"
          label={
            <Typography noWrap fontFamily="monospace" variant="caption">
              {namespace}
            </Typography>
          }
          size="small"
          sx={responsiveChipStyle}
          variant="outlined"
        />
      </Tooltip>
      {hasReplicaSets && (
        <Chip
          data-test-replica-set-count-chip
          color={color}
          label={
            <Typography
              fontFamily="monospace"
              variant="caption"
            >{`${replicaSetCount} ${pluralize(
              'ReplicaSet',
              replicaSetCount
            )}`}</Typography>
          }
          size="small"
          variant="outlined"
        />
      )}
      {hasReplicationControllers && (
        <Chip
          data-test-replication-controller-count-chip
          color={color}
          label={
            <Typography
              fontFamily="monospace"
              variant="caption"
            >{`${replicationControllerCount} ${pluralize(
              'ReplicationController',
              replicationControllerCount
            )}`}</Typography>
          }
          size="small"
          variant="outlined"
        />
      )}
      {hasPods && (
        <Chip
          data-test-pod-count-chip
          color={color}
          label={
            <Typography fontFamily="monospace" variant="caption">
              {`${podCount} ${pluralize('Pod', podCount)}`}
            </Typography>
          }
          size="small"
          variant="outlined"
        />
      )}
    </Stack>
  )
}
